import type { Faro } from '@grafana/faro-web-sdk';
import { getConfig } from '../config';

let faroInstance: Faro | undefined;
let initializing = false;

// Two separate Grafana Cloud Frontend Observability app registrations —
// production ("curi live", appId 1983) and staging ("curi live staging",
// appId 2011) — so the two environments' data/dashboards stay fully
// separate rather than just filterable by `app.environment` within one app.
// Must exactly match the `--app-name` (and `--app-id`) passed to
// `faro-cli inject-bundle-id` / `upload` for the corresponding environment
// in .github/workflows/deploy-{dev,production}.yml. Faro auto-detects the
// build's bundle id by reading a `__faroBundleId_<appName>` global that
// `inject-bundle-id` sets on the built JS files — a mismatch here means
// source maps silently never correlate to errors.
const getFaroAppName = () =>
  getConfig().unleash.environment === 'production'
    ? 'curi live'
    : 'curi live staging';

/**
 * Faro's `url` needs to be absolute, and must always be this app's own
 * origin — the browser never talks to Alloy directly (same reasoning as the
 * Unleash proxy at `/api/ff/proxy`: avoid ad-blockers/content filters, hide
 * the vendor).
 */
const getCollectorProxyUrl = () => `${window.location.origin}/api/tel/proxy`;

/**
 * Known-noisy error patterns that aren't worth spending Alloy's rate budget
 * on (Faro's own `ignoreErrors` — no need to hand-roll a denylist check).
 */
const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /^Script error\.?$/,
];

/**
 * Fraction of *sessions* (not events) that send any telemetry at all. The
 * fastest lever to pull (env var + redeploy, no code change) if Alloy ever
 * gets overloaded post-launch.
 */
const getSessionSamplingRate = () => {
  const raw = process.env.NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE;
  const parsed = raw ? Number(raw) : 1;
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
};

/**
 * Flipped by `forceFullSession()` (see useTelemetry.tsx) for a session that
 * must not be lost to `getSessionSamplingRate()`.
 *
 * A plain overwrite of the session's `isSampled` attribute isn't enough:
 * Faro's own SessionInstrumentation reconciles the live session against
 * sessionStorage on every `setSession()` call, and whenever it sees the
 * attributes differ from storage it re-draws `isSampled()` from scratch —
 * silently reverting a one-off attribute poke back to the configured rate's
 * verdict. Routing the override through the `sampler` below means that
 * re-draw also sees the override, instead of undoing it.
 */
let forceFullSampling = false;

export const setForceFullSampling = (): void => {
  forceFullSampling = true;
};

/** Exported so tests can wire the exact same sampler into their own Faro instance. */
export const fullSessionSampler = (): number | undefined =>
  forceFullSampling ? 1 : undefined;

/**
 * Initializes the Faro browser SDK exactly once per page load, replacing
 * the old New Relic Browser agent. Safe to call from multiple components —
 * only the first call does anything.
 *
 * `@grafana/faro-web-sdk` is loaded via dynamic `import()` rather than a
 * static top-level import: every call site that touches telemetry
 * (`useTelemetry.tsx`, and everything that imports it — `responseSync.ts`,
 * `gameAPI.ts`, etc.) would otherwise pull the whole SDK — and, transitively,
 * a `web-vitals` sub-dependency shipped as ESM-only — into every module
 * graph that references them, including Jest's CJS test environment, which
 * can't parse it. A dynamic import only loads it when this function actually
 * runs (i.e. in the browser, from `TelemetryInit`), not merely referenced.
 */
export const initTelemetry = (): void => {
  if (typeof window === 'undefined') return;
  if (faroInstance || initializing) return;
  initializing = true;

  void import('@grafana/faro-web-sdk')
    .then(({ initializeFaro, getWebInstrumentations }) => {
      faroInstance = initializeFaro({
        url: getCollectorProxyUrl(),
        app: {
          name: getFaroAppName(),
          version: process.env.NEXT_PUBLIC_SHA8,
          environment: getConfig().unleash.environment,
        },
        sessionTracking: {
          samplingRate: getSessionSamplingRate(),
          sampler: fullSessionSampler,
        },
        // Faro's own dedupe + ignore-list — no need to hand-roll either.
        dedupe: true,
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        instrumentations: getWebInstrumentations({
          captureConsole: false,
          // Reports one `faro.performance.resource` event per fetch/XHR
          // call the app makes (join, postResponse/patchResponse, the 5s
          // voting-result poll, ...) — a volume source on top of our own
          // explicit metrics, without adding signal we're currently using.
          // Disabled deliberately; revisit if per-request timing breakdowns
          // become worth the extra events.
          enablePerformanceInstrumentation: false,
        }),
      });
    })
    .finally(() => {
      initializing = false;
    });
};

export const getFaro = () => faroInstance;
